'use client';

import { createStore } from 'zustand';

import { EnvironmentNameEnum, getEnvironmentNameFromUrl } from '@/config/environment';
import { AuthenticatedUser } from '@/types/auth-base.types';
import { tryToLoadAuthenticatedUser } from '@/services/auth/user.service';
import { getAuthTokens, removeAuthTokens } from '@/storage/auth-tokens.storage';
import { setAuthSessionHint, removeAuthSessionHint } from '@/storage/auth-session-hint.storage';
import { resetCart } from '@/store/cart/cart.store';
import { closePatientSelection } from '@/store/patient-selection/patient-selection.store';
import { clearLogIdentity, setLogIdentity } from '@/logger/identity.client';
import { HttpException } from '@/utils/api/api-client';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthStoreState = {
  authenticatedUser: AuthenticatedUser | undefined;
  authStatus: AuthStatus;
  phoneConfirmedAt: string | undefined;
};

export type ComputedAuthStoreState = {
  computed: {
    isLoggedIn: boolean;
    isAuthReady: boolean;
  };
};

const initialAuthStatus: AuthStatus
  = typeof window !== 'undefined' && getAuthTokens()?.access_token ? 'loading' : 'unauthenticated';

export const authStore = createStore<AuthStoreState & ComputedAuthStoreState>((set, get) => {
  return {
    authenticatedUser: undefined,
    authStatus: initialAuthStatus,
    phoneConfirmedAt: undefined,
    computed: {
      get isLoggedIn() {
        return !!get().authenticatedUser;
      },
      get isAuthReady() {
        return get().authStatus !== 'loading';
      },
    },
  };
});

export const setAuthenticatedUser = (user: AuthenticatedUser | undefined) => {
  const { authenticatedUser: previousUser } = authStore.getState();
  const isSameCustomer = Boolean(user?.id) && user?.id === previousUser?.id;

  authStore.setState({
    authenticatedUser: user,
    authStatus: user ? 'authenticated' : 'unauthenticated',
    ...(isSameCustomer ? {} : { phoneConfirmedAt: undefined }),
  });
  setAuthSessionHint(!!user);
  setLogIdentity(user?.id);
};

if (typeof window !== 'undefined' && getEnvironmentNameFromUrl() === EnvironmentNameEnum.LOCAL) {
  (window as unknown as { __medprevAuthStore?: typeof authStore }).__medprevAuthStore = authStore;
}

export const confirmCustomerPhone = (phone: string) => {
  const { authenticatedUser } = authStore.getState();

  authStore.setState({
    phoneConfirmedAt: new Date().toISOString(),
    ...(authenticatedUser
      ? {
          authenticatedUser: {
            ...authenticatedUser,
            contact: {
              email: authenticatedUser.contact?.email ?? '',
              phones: [phone, ...(authenticatedUser.contact?.phones ?? []).slice(1)],
            },
          },
        }
      : {}),
  });
};

export async function logoutUser() {
  authStore.setState({
    authenticatedUser: undefined,
    authStatus: 'unauthenticated',
    phoneConfirmedAt: undefined,
  });
  removeAuthTokens();
  removeAuthSessionHint();
  clearLogIdentity();
  resetCart();
  closePatientSelection();
}

function markSessionUnresolved() {
  authStore.setState({
    authenticatedUser: undefined,
    authStatus: 'unauthenticated',
    phoneConfirmedAt: undefined,
  });
  setAuthSessionHint(false);
  clearLogIdentity();
  resetCart();
  closePatientSelection();
}

function isAuthenticationFailure(error: unknown): boolean {
  return error instanceof HttpException && (error.status === 401 || error.status === 403);
}

export async function resolveAuthenticatedUser(accessToken?: string) {
  const token = accessToken || getAuthTokens()?.access_token;
  if (!token) {
    markSessionUnresolved();
    return;
  }

  let user: AuthenticatedUser | undefined;

  try {
    user = await tryToLoadAuthenticatedUser(token);
  } catch (error) {
    if (isAuthenticationFailure(error)) {
      markSessionUnresolved();
      return;
    }

    throw error;
  }

  if (!user) {
    markSessionUnresolved();
    return;
  }
  setAuthenticatedUser(user);
}
